const CryptoJS = require("crypto-js");

/**
 * Normalizes the API path for hash generation
 * Ensures all paths have the format: /api/method/freshdi.data.web.order.user
 * Different from URL construction - this is only for hash consistency
 */
const normalizePathForHash = (path, baseURL) => {
  // Remove any leading/trailing whitespace
  const cleanPath = path.trim();

  // If path already starts with /api/method/, return as is
  if (cleanPath.startsWith("/api/method/")) {
    return cleanPath;
  }

  // If path starts with / but not /api/method/, it might be a custom path
  // Keep it as is for non-API paths
  if (cleanPath.startsWith("/") && !cleanPath.startsWith("/api/")) {
    return cleanPath;
  }

  // If path starts with /api/ but not /api/method/, normalize it
  if (cleanPath.startsWith("/api/") && !cleanPath.startsWith("/api/method/")) {
    // Extract the method part after /api/
    const methodPart = cleanPath.replace(/^\/api\//, "");
    return `/api/method/${methodPart}`;
  }

  // Handle baseURL cases
  if (baseURL) {
    // If baseURL already contains /api/method, the path should be just the method name
    if (baseURL.includes("/api/method")) {
      // If path doesn't start with /, add it
      return cleanPath.startsWith("/") ? cleanPath : `/${cleanPath}`;
    }
  }

  // Default case: treat as method name and add /api/method/ prefix
  // Remove leading slash if present to avoid double slashes
  const methodName = cleanPath.startsWith("/")
    ? cleanPath.substring(1)
    : cleanPath;
  return `/api/method/${methodName}`;
};

/**
 * Filters out undefined values, empty strings, null values, and "undefined"/"null" strings
 */
const filterUndefinedAndEmptyValues = (obj) => {
  return Object.fromEntries(
    Object.entries(obj).filter(([key, value]) => {
      // Remove if value is undefined
      if (value === undefined) return false;

      // Remove if value is null
      if (value === null) return false;

      // Remove if value is empty string
      if (value === "") return false;

      // Remove if value is string "undefined"
      if (value === "undefined") return false;

      // Remove if value is string "null"
      if (value === "null") return false;

      // Keep all other values
      return true;
    })
  );
};

/**
 * Converts all object values to strings
 */
const stringifyObjectValues = (obj) => {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [key, String(value)])
  );
};

/**
 * Filters out token-related keys from params
 */
const filterTokenKeys = (params) => {
  return Object.fromEntries(
    Object.entries(params).filter(
      ([key]) => !key.toLowerCase().includes("token")
    )
  );
};

/**
 * Processes body data for payload generation
 * Always keeps original data types
 */
const processBodyData = (body) => {
  return JSON.stringify(body);
};

/**
 * Processes params data for payload generation
 * Removes undefined/null/empty values, then converts params values to strings
 */
const processParamsData = (params, filterTokens = true) => {
  // First filter out undefined, null, and empty values
  const filteredUndefinedParams = filterUndefinedAndEmptyValues(params);

  // Then filter token keys if needed
  const processedParams = filterTokens
    ? filterTokenKeys(filteredUndefinedParams)
    : filteredUndefinedParams;

  // Finally stringify values
  const stringifiedParams = stringifyObjectValues(processedParams);

  return {
    paramsString: JSON.stringify(stringifiedParams),
    filteredParams: stringifiedParams,
  };
};
/**
 * Creates the payload string for hash generation
 */
const createPayloadString = (
  timestamp,
  method,
  normalizedPath,
  bodyString,
  paramsString
) => {
  const cleanString = (str) => String(str).trim().replace(/\n/g, " ");
  return `${cleanString(timestamp)}|${cleanString(method)}|${cleanString(
    normalizedPath
  )}|${cleanString(bodyString)}|${cleanString(paramsString)}`;
};

/**
 * Generates HMAC SHA256 hash
 */
const generateHash = (payload, secretKey) => {
  return CryptoJS.HmacSHA256(payload, secretKey).toString();
};

/**
 * Generates a control hash for API request security
 */
const generateRequestPayload = (
  path,
  method,
  body = {},
  params = {},
  timestamp,
  secretKey,
  filterTokens = true
) => {
  const normalizedPath = normalizePathForHash(path);
  const bodyString = processBodyData(body);
  const { paramsString, filteredParams } = processParamsData(
    params,
    filterTokens
  );

  const payload = createPayloadString(
    timestamp,
    method,
    normalizedPath,
    bodyString,
    paramsString
  );

  const controlHash = generateHash(payload, secretKey);

  return {
    payload,
    controlHash,
    timestamp,
    filteredParams,
  };
};

module.exports = { generateRequestPayload };
